import {
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  CheckBadgeIcon,
  CurrencyDollarIcon,
  ArrowPathRoundedSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import {
  useOpsClients,
  useOnboardingMetrics,
  useOperationsMetrics,
  useOutstandingDocuments,
  useComplianceDeadlines,
  useLeads,
  useSubscriptionPayments,
  useBillingSummary,
  usePayments,
  useAtRisk,
  useJourney,
} from '../hooks/useClerkOps';
import {
  formatMoney,
  type JourneyMilestone,
  type JourneyMilestoneState,
  type OpsClient,
} from '../services/clerkOpsService';
import ClientDetailModal from './ClientDetailModal';

// A milestone that has slipped its internal target is a prompt for us, never a
// customer lockout — their trial runs its full length either way.
const MILESTONE_TONE: Record<JourneyMilestoneState, string> = {
  done: 'text-success',
  ready: 'text-warning',
  due: 'text-ink-3',
  overdue: 'text-danger',
  blocked: 'text-ink-3',
};

const MILESTONE_LABEL: Record<JourneyMilestoneState, string> = {
  done: 'Done',
  ready: 'Ready to send',
  due: 'Due',
  overdue: 'Overdue',
  blocked: 'Waiting',
};

function MilestoneCell({ m }: { m: JourneyMilestone | undefined }) {
  if (!m) return <span className="text-ink-3">—</span>;
  return (
    <span className={MILESTONE_TONE[m.state]}>
      {MILESTONE_LABEL[m.state]}
      {m.detail ? <span className="text-ink-3"> · {m.detail}</span> : null}
    </span>
  );
}

const HEALTH_TONE: Record<string, string> = {
  healthy: 'text-success',
  at_risk: 'text-warning',
  critical: 'text-danger',
};

function pct(v: number | null): string {
  return v == null ? '—' : `${Math.round(v * 100)}%`;
}
function days(v: number | null): string {
  return v == null ? '—' : `${v.toFixed(1)}d`;
}
function fmtDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
}
function fmtRelative(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 30 ? `${days}d ago` : d.toLocaleDateString();
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'default' | 'warn' | 'danger';
}) {
  const valueColor =
    tone === 'danger' ? 'text-danger' : tone === 'warn' ? 'text-warning' : 'text-ink';
  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-s">
      <div className="flex items-center gap-2 text-ink-2">
        <Icon className="h-5 w-5" />
        <span className="text-sm">{label}</span>
      </div>
      <div className={`mt-2 font-display text-2xl font-bold ${valueColor}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-ink-3">{sub}</div>}
    </div>
  );
}

export default function ClerkOpsConsole() {
  const { data: clients = [] } = useOpsClients();
  const { data: onb, isLoading: loadingOnb } = useOnboardingMetrics();
  const { data: ops, isLoading: loadingOps } = useOperationsMetrics();
  const { data: docs = [] } = useOutstandingDocuments();
  const { data: deadlines = [] } = useComplianceDeadlines(30);
  const { data: leads = [] } = useLeads(30);
  const { data: subscriptionPayments = [] } = useSubscriptionPayments('pending');
  const { data: billing } = useBillingSummary();
  const { data: payments = [] } = usePayments();
  const { data: atRisk = [] } = useAtRisk();
  const { data: journey } = useJourney();
  const [selectedClient, setSelectedClient] = useState<OpsClient | null>(null);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Clerk Operations</h1>
        <p className="mt-1 text-sm text-ink-2">
          Onboarding &amp; accounting operations across your book of clients.
        </p>
      </div>

      {/* Activation journey */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
          Activation Journey
          {journey ? (
            <span className="ml-2 font-normal normal-case text-ink-3">
              {journey.stalled} of {journey.total} missed a target · {journey.awaitingPnl} awaiting a
              P&amp;L
            </span>
          ) : null}
        </h2>
        <p className="text-xs text-ink-3">
          Signup → bank statement (24h) → P&amp;L back (48h) → subscribed (7d). Internal targets —
          a red cell means someone should act today, not that the customer is locked out.
        </p>
        <div className="overflow-hidden rounded-card border border-line bg-surface shadow-s">
          {!journey || journey.rows.length === 0 ? (
            <EmptyRow text="No clients in the funnel yet." />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 font-medium">Statement</th>
                  <th className="px-4 py-2 font-medium">P&amp;L</th>
                  <th className="px-4 py-2 font-medium">Subscribed</th>
                  <th className="px-4 py-2 font-medium">What&apos;s blocking them</th>
                </tr>
              </thead>
              <tbody>
                {journey.rows.map((r) => (
                  <tr key={r.clientId} className="border-b border-line align-top">
                    <td className="px-4 py-2">
                      <div className="text-ink">{r.name}</div>
                      <div className="text-xs text-ink-3">
                        day {r.daysSinceSignup}
                        {r.trialDaysLeft != null
                          ? r.trialDaysLeft < 0
                            ? ` · trial ended ${Math.abs(r.trialDaysLeft)}d ago`
                            : ` · trial ${r.trialDaysLeft}d left`
                          : ''}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <MilestoneCell m={r.milestones.find((m) => m.key === 'statement')} />
                    </td>
                    <td className="px-4 py-2">
                      <MilestoneCell m={r.milestones.find((m) => m.key === 'pnl')} />
                    </td>
                    <td className="px-4 py-2">
                      <MilestoneCell m={r.milestones.find((m) => m.key === 'subscription')} />
                    </td>
                    <td className="px-4 py-2 text-xs text-ink-2">
                      {r.issues.length === 0 ? (
                        <span className="text-ink-3">—</span>
                      ) : (
                        <ul className="space-y-0.5">
                          {r.issues.map((issue) => (
                            <li key={issue}>• {issue}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Clients roster */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
          Clients ({clients.length})
        </h2>
        <div className="overflow-hidden rounded-card border border-line bg-surface shadow-s">
          {clients.length === 0 ? (
            <EmptyRow text="No clients yet." />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Health</th>
                  <th className="px-4 py-2 font-medium">Features</th>
                  <th className="px-4 py-2 font-medium">Last Active</th>
                  <th className="px-4 py-2 font-medium">Latest Action</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr
                    key={c.clientId}
                    onClick={() => setSelectedClient(c)}
                    className="cursor-pointer border-b border-line hover:bg-black/[0.04]"
                  >
                    <td className="px-4 py-2 font-medium text-ink">{c.name ?? c.clientId}</td>
                    <td className="px-4 py-2 text-ink-2">{c.status ?? '—'}</td>
                    <td className={`px-4 py-2 ${HEALTH_TONE[c.health ?? ''] ?? 'text-ink-3'}`}>
                      {c.health ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-ink-2">
                      {c.featureProgress ? (
                        <FeatureBar used={c.featureProgress.used} total={c.featureProgress.total} />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-2 text-ink-3">{fmtRelative(c.lastActiveAt)}</td>
                    <td className="px-4 py-2 text-ink-3">{c.lastAction?.label ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Onboarding */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">Onboarding</h2>
        {loadingOnb ? (
          <SkeletonRow />
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <StatCard icon={AcademicCapIcon} label="In Onboarding" value={onb?.inOnboarding ?? 0} />
            <StatCard
              icon={CheckBadgeIcon}
              label="Completion (30d)"
              value={pct(onb?.completionRate30d ?? null)}
            />
            <StatCard
              icon={ClockIcon}
              label="Avg. to Complete"
              value={days(onb?.avgTimeToCompleteDays ?? null)}
            />
            <StatCard
              icon={ArrowTrendingUpIcon}
              label="Time to First Value"
              value={days(onb?.avgTimeToFirstValueDays ?? null)}
            />
            <StatCard
              icon={ExclamationTriangleIcon}
              label="Stalled"
              value={onb?.stalled.count ?? 0}
              tone={(onb?.stalled.count ?? 0) > 0 ? 'warn' : 'default'}
            />
          </div>
        )}
      </section>

      {/* Operations */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">Operations</h2>
        {loadingOps ? (
          <SkeletonRow />
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <StatCard
              icon={BanknotesIcon}
              label="Payroll Runs"
              value={ops?.payrollRunsCompleted ?? 0}
            />
            <StatCard
              icon={BoltIcon}
              label="Books Closed"
              value={`${ops?.booksClosedThisMonth ?? 0}/${ops?.booksTotalThisMonth ?? 0}`}
              sub="this month"
            />
            <StatCard
              icon={CheckBadgeIcon}
              label="Filing Success"
              value={pct(ops?.filingSuccessRate ?? null)}
            />
            <StatCard
              icon={DocumentTextIcon}
              label="Docs Awaiting"
              value={ops?.documentsAwaitingCustomer ?? 0}
              tone={(ops?.documentsAwaitingCustomer ?? 0) > 0 ? 'warn' : 'default'}
            />
            <StatCard
              icon={ExclamationTriangleIcon}
              label="Deadlines (7d)"
              value={ops?.complianceDeadlinesDue7d ?? 0}
              tone={(ops?.complianceDeadlinesDue7d ?? 0) > 0 ? 'danger' : 'default'}
            />
          </div>
        )}
      </section>

      {/* Outstanding documents */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
          Documents Awaiting Customer
        </h2>
        <div className="overflow-hidden rounded-card border border-line bg-surface shadow-s">
          {docs.length === 0 ? (
            <EmptyRow text="No outstanding documents." />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">Document</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Requested</th>
                  <th className="px-4 py-2 font-medium">Due</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d, i) => (
                  <tr key={`${d.clientId}-${d.documentType}-${i}`} className="border-b border-line">
                    <td className="px-4 py-2 text-ink">{d.label}</td>
                    <td className="px-4 py-2 text-ink-2">{d.status ?? '—'}</td>
                    <td className="px-4 py-2 text-ink-3">{fmtDate(d.requestedAt)}</td>
                    <td className="px-4 py-2 text-ink-3">{fmtDate(d.dueAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Compliance deadlines */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
          Compliance Deadlines (next 30 days)
        </h2>
        <div className="overflow-hidden rounded-card border border-line bg-surface shadow-s">
          {deadlines.length === 0 ? (
            <EmptyRow text="No upcoming deadlines." />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">Deadline</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Due</th>
                </tr>
              </thead>
              <tbody>
                {deadlines.map((d, i) => (
                  <tr key={`${d.clientId}-${d.deadlineType}-${i}`} className="border-b border-line">
                    <td className="px-4 py-2 text-ink">{d.label}</td>
                    <td className="px-4 py-2 text-ink-2">{d.deadlineType ?? '—'}</td>
                    <td className="px-4 py-2 text-ink-2">{d.status ?? '—'}</td>
                    <td className="px-4 py-2 text-ink-3">{fmtDate(d.dueAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Demo requests */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
          Demo Requests (last 30 days)
        </h2>
        <div className="overflow-hidden rounded-card border border-line bg-surface shadow-s">
          {leads.length === 0 ? (
            <EmptyRow text="No demo requests in this window." />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Contact</th>
                  <th className="px-4 py-2 font-medium">Business</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-line align-top">
                    <td className="whitespace-nowrap px-4 py-2 text-ink-3">{fmtDate(l.createdAt)}</td>
                    <td className="px-4 py-2 text-ink">{l.name}</td>
                    <td className="px-4 py-2">
                      <a href={`mailto:${l.email}`} className="text-brand hover:underline">
                        {l.email}
                      </a>
                      {l.phone && <div className="text-xs text-ink-3">{l.phone}</div>}
                    </td>
                    <td className="px-4 py-2 text-ink-2">
                      {l.businessName ?? '—'}
                      {l.companySize && <div className="text-xs text-ink-3">{l.companySize}</div>}
                    </td>
                    <td className="px-4 py-2 text-ink-2 capitalize">{l.source ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Subscription payments awaiting verification */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
          Subscription Payments (awaiting verification)
        </h2>
        <div className="overflow-hidden rounded-card border border-line bg-surface shadow-s">
          {subscriptionPayments.length === 0 ? (
            <EmptyRow text="No payments awaiting verification. Verify in the Clerk admin dashboard." />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">Submitted</th>
                  <th className="px-4 py-2 font-medium">Business</th>
                  <th className="px-4 py-2 font-medium">Reference</th>
                  <th className="px-4 py-2 font-medium">Tiers</th>
                  <th className="px-4 py-2 font-medium">Cycle</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {subscriptionPayments.map((p) => (
                  <tr key={p.id} className="border-b border-line align-top">
                    <td className="whitespace-nowrap px-4 py-2 text-ink-3">{fmtDate(p.submittedAt)}</td>
                    <td className="px-4 py-2 text-ink">{p.tenantName ?? '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs text-ink-2">{p.reference}</td>
                    <td className="px-4 py-2 text-ink-2 capitalize">{p.tiers.join(' + ') || '—'}</td>
                    <td className="px-4 py-2 text-ink-2 capitalize">{p.cycle ?? '—'}</td>
                    <td className="px-4 py-2 font-mono tabular-nums text-ink">
                      ₦{Number(p.amountNgn).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-xs text-ink-3">
          Read-only here — verify a payment (which activates the customer&apos;s subscription) from the Clerk admin
          dashboard.
        </p>
      </section>

      {/* Billing */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">Billing</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <StatCard icon={ArrowTrendingUpIcon} label="MRR" value={formatMoney(billing?.mrr ?? null)} />
          <StatCard
            icon={CurrencyDollarIcon}
            label="Revenue (mo)"
            value={formatMoney(billing?.revenueThisMonth ?? null)}
            sub={billing?.revenuePrevMonth ? `prev ${formatMoney(billing.revenuePrevMonth)}` : undefined}
          />
          <StatCard
            icon={BanknotesIcon}
            label="Outstanding"
            value={formatMoney(billing?.outstandingTotal ?? null)}
            tone={Number(billing?.outstandingTotal?.amount ?? 0) > 0 ? 'warn' : 'default'}
          />
          <StatCard icon={CheckBadgeIcon} label="Active Subs" value={billing?.activeSubscriptions ?? 0} />
          <StatCard
            icon={XCircleIcon}
            label="Failed Payments"
            value={billing?.failedPaymentsCount ?? 0}
            tone={(billing?.failedPaymentsCount ?? 0) > 0 ? 'danger' : 'default'}
          />
          <StatCard
            icon={ArrowPathRoundedSquareIcon}
            label="Refunds (mo)"
            value={billing?.refundsThisMonth?.count ?? 0}
            sub={billing?.refundsThisMonth?.amount ? formatMoney(billing.refundsThisMonth.amount) : undefined}
          />
        </div>

        {/* At-risk (dunning) */}
        {atRisk.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-danger/30 bg-surface shadow-s">
            <div className="border-b border-line px-4 py-2 text-sm font-medium text-danger">
              At risk — payment issues ({atRisk.length})
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 font-medium">Reason</th>
                  <th className="px-4 py-2 font-medium">Overdue</th>
                  <th className="px-4 py-2 font-medium">Days</th>
                  <th className="px-4 py-2 font-medium">Attempts</th>
                </tr>
              </thead>
              <tbody>
                {atRisk.map((a, i) => (
                  <tr key={`${a.clientId}-${i}`} className="border-b border-line">
                    <td className="px-4 py-2 text-ink">{clientName(clients, a.clientId)}</td>
                    <td className="px-4 py-2 text-ink-2">{a.reason ?? '—'}</td>
                    <td className="px-4 py-2 text-warning">{formatMoney(a.amountOverdue)}</td>
                    <td className="px-4 py-2 text-ink-3">{a.daysOverdue}</td>
                    <td className="px-4 py-2 text-ink-3">{a.failedAttempts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Recent payments */}
        <div className="overflow-hidden rounded-card border border-line bg-surface shadow-s">
          <div className="border-b border-line px-4 py-2 text-sm text-ink-3">Recent payments</div>
          {payments.length === 0 ? (
            <EmptyRow text="No payments yet." />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Method</th>
                  <th className="px-4 py-2 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.paymentId} className="border-b border-line">
                    <td className="px-4 py-2 text-ink">{clientName(clients, p.clientId)}</td>
                    <td className="px-4 py-2 text-ink">{formatMoney(p.amount)}</td>
                    <td className={`px-4 py-2 ${PAYMENT_TONE[p.status ?? ''] ?? 'text-ink-2'}`}>
                      {p.status ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-ink-3">{p.method ?? '—'}</td>
                    <td className="px-4 py-2 text-ink-3">{fmtRelative(p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <ClientDetailModal client={selectedClient} onClose={() => setSelectedClient(null)} />
    </div>
  );
}

const PAYMENT_TONE: Record<string, string> = {
  succeeded: 'text-success',
  failed: 'text-danger',
  refunded: 'text-warning',
  pending: 'text-ink-2',
};

function clientName(clients: { clientId: string; name: string | null }[], id: string | null): string {
  if (!id) return '—';
  return clients.find((c) => c.clientId === id)?.name ?? id;
}

function FeatureBar({ used, total }: { used: number; total: number }) {
  const frac = total > 0 ? used / total : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 overflow-hidden rounded-full bg-surface-2">
        <div className="h-full bg-brand" style={{ width: `${Math.round(frac * 100)}%` }} />
      </div>
      <span className="text-xs text-ink-3">
        {used}/{total}
      </span>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-24 animate-pulse rounded-card border border-line bg-surface" />
      ))}
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <div className="px-4 py-8 text-center text-sm text-ink-3">{text}</div>;
}
